import {
    createHandlerChainRunner,
    HandlerWithResult,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { CONTACT_US_FORM_FIELDS } from "@thkStore/Constants/ContactUs/formFields";

interface ProductInfo {
    productId: string;
    productName: string;
    productNumber: string;
    productUrl: string;
    productImage: string;
}

type SubmitContactFormWithProductParameter = {
    emailRecipients: string;
    product?: ProductInfo;
    attachments?: File[];
    onException?: (error: Error) => void;
} & HasOnSuccess;

type HandlerType = HandlerWithResult<SubmitContactFormWithProductParameter, void>;

export const SubmitForm: HandlerType = async props => {
    const state = props.getState().components.contactUsForm;
    const fd = new FormData();

    fd.append(CONTACT_US_FORM_FIELDS.firstName,    state.fieldValues["firstName"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.lastName,     state.fieldValues["lastName"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.emailAddress, state.fieldValues["emailAddress"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.phoneNumber,  state.fieldValues["phoneNumber"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.topic,        state.fieldValues["topic"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.companyName,  state.fieldValues["companyName"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.message,      state.fieldValues["message"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.city,         state.fieldValues["city"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.state,        state.fieldValues["state"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.country,      state.fieldValues["country"] || "");
    fd.append(CONTACT_US_FORM_FIELDS.emailTo,      props.parameter.emailRecipients);

    if (props.parameter.product) {
        fd.append(CONTACT_US_FORM_FIELDS.productId,     props.parameter.product.productId);
        fd.append(CONTACT_US_FORM_FIELDS.productName,   props.parameter.product.productName);
        fd.append(CONTACT_US_FORM_FIELDS.productNumber, props.parameter.product.productNumber);
        fd.append(CONTACT_US_FORM_FIELDS.productUrl,    props.parameter.product.productUrl);
        fd.append(CONTACT_US_FORM_FIELDS.productImage,  props.parameter.product.productImage);
    }

    (props.parameter.attachments ?? []).forEach(file => {
        fd.append(CONTACT_US_FORM_FIELDS.attachment, file, file.name);
    });

    let response: Response;
    try {
        response = await fetch("/api/v1/contactus/submit", {
            method: "POST",
            body: fd,
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" },
        });
    } catch (networkError) {
        props.parameter.onException?.(networkError as Error);
        return;
    }

    if (!response.ok) {
        let userMessage = `Submission failed (HTTP ${response.status})`;
        const body = await response.json();
        if (body?.message) {
            userMessage = body.message;
        }
        props.parameter.onException?.(new Error(userMessage));
        return;
    }

    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [SubmitForm];

const submitContactFormWithProduct = createHandlerChainRunner(chain, "SubmitContactFormWithProduct");
export default submitContactFormWithProduct;
